import { useState, FormEvent } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/context/AuthContext';

interface NewPostModalProps {
  onClose: () => void;
  onPostCreated: () => void;
}

const CATEGORIES = [
  { id: 'futbol',   label: 'Fútbol' },
  { id: 'tenis',    label: 'Tenis' },
  { id: 'basket',   label: 'Baloncesto' },
  { id: 'analisis', label: 'Análisis' },
];

export default function NewPostModal({ onClose, onPostCreated }: NewPostModalProps) {
  const { supabaseUser } = useAuth();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('futbol');
  const [tagsInput, setTagsInput] = useState('');
  const [isPrediction, setIsPrediction] = useState(false);
  const [predictionMarket, setPredictionMarket] = useState('');
  const [predictionOdds, setPredictionOdds] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!supabaseUser) return;
    if (!title.trim() || !content.trim()) {
      setError('El título y el contenido son obligatorios.');
      return;
    }
    if (content.length > 2000) {
      setError('El contenido no puede superar los 2000 caracteres.');
      return;
    }

    setSubmitting(true);
    setError('');

    const cat = CATEGORIES.find((c) => c.id === category);
    const tags = tagsInput
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter((t) => t.length > 0 && t.length <= 30)
      .slice(0, 5);

    const { error: insertError } = await supabase.from('posts').insert({
      user_id: supabaseUser.id,
      title: title.trim(),
      content: content.trim(),
      category,
      category_label: cat?.label ?? 'Fútbol',
      tags,
      is_prediction: isPrediction,
      prediction_market: isPrediction && predictionMarket ? predictionMarket.trim() : null,
      prediction_odds: isPrediction && predictionOdds ? parseFloat(predictionOdds) : null,
      prediction_result: isPrediction ? 'pending' : null,
    });

    setSubmitting(false);

    if (insertError) {
      setError('Error al publicar. Inténtalo de nuevo.');
    } else {
      onPostCreated();
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-white font-bold text-lg">Nuevo Post</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-[#6b7280] hover:text-white cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              type="text"
              placeholder="Título del análisis..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              className="w-full bg-[#0d0f14] border border-[#2a2d3a] text-white text-sm rounded-xl px-4 py-3 placeholder-[#6b7280] focus:outline-none focus:border-[#00e87a]/40"
            />
            <p className="text-[#3d4149] text-xs mt-1 text-right">{title.length}/120</p>
          </div>

          <div>
            <textarea
              rows={5}
              placeholder="Escribe tu análisis o pick... (máx. 2000 caracteres)"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              maxLength={2000}
              className="w-full bg-[#0d0f14] border border-[#2a2d3a] text-white text-sm rounded-xl px-4 py-3 placeholder-[#6b7280] focus:outline-none focus:border-[#00e87a]/40 resize-none"
            />
            <p className="text-[#3d4149] text-xs mt-1 text-right">{content.length}/2000</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[#6b7280] text-xs mb-1 block">Categoría</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-[#0d0f14] border border-[#2a2d3a] text-white text-sm rounded-xl px-3 py-2.5 cursor-pointer focus:outline-none"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[#6b7280] text-xs mb-1 block">Tags (separados por coma)</label>
              <input
                type="text"
                placeholder="LaLiga, corners..."
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                className="w-full bg-[#0d0f14] border border-[#2a2d3a] text-white text-sm rounded-xl px-3 py-2.5 placeholder-[#6b7280] focus:outline-none focus:border-[#00e87a]/40"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <div
              onClick={() => setIsPrediction(!isPrediction)}
              className={`w-10 h-5 rounded-full transition-colors relative flex-shrink-0 ${
                isPrediction ? 'bg-[#00e87a]' : 'bg-[#2a2d3a]'
              }`}
            >
              <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                isPrediction ? 'translate-x-5' : 'translate-x-0.5'
              }`}></div>
            </div>
            <span className="text-[#a8b2c1] text-sm">Este post incluye un pick</span>
          </label>

          {isPrediction && (
            <div className="grid grid-cols-2 gap-3 p-4 bg-[#0d0f14] border border-[#2a2d3a] rounded-xl">
              <div>
                <label className="text-[#6b7280] text-xs mb-1 block">Mercado</label>
                <input
                  type="text"
                  placeholder="Over 2.5 Goles"
                  value={predictionMarket}
                  onChange={(e) => setPredictionMarket(e.target.value)}
                  className="w-full bg-[#151719] border border-[#2a2d3a] text-white text-sm rounded-lg px-3 py-2 placeholder-[#6b7280] focus:outline-none focus:border-[#00e87a]/40"
                />
              </div>
              <div>
                <label className="text-[#6b7280] text-xs mb-1 block">Cuota</label>
                <input
                  type="number"
                  step="0.01"
                  min="1.01"
                  max="100"
                  placeholder="1.85"
                  value={predictionOdds}
                  onChange={(e) => setPredictionOdds(e.target.value)}
                  className="w-full bg-[#151719] border border-[#2a2d3a] text-white text-sm rounded-lg px-3 py-2 placeholder-[#6b7280] focus:outline-none focus:border-[#00e87a]/40"
                />
              </div>
            </div>
          )}

          {error && (
            <p className="text-[#ef4444] text-xs">{error}</p>
          )}

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 border border-[#2a2d3a] text-[#a8b2c1] text-sm rounded-xl hover:bg-white/5 cursor-pointer whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-3 bg-[#00e87a] text-black text-sm font-bold rounded-xl hover:bg-[#00d070] cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {submitting ? 'Publicando...' : 'Publicar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
